import AddIcon from '@mui/icons-material/Add';
import {
  Autocomplete,
  AutocompleteOwnerState,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import _debounce from 'lodash/debounce';
import { useEffect, useState } from 'react';

interface AutocompleteWithThrottleProps<T> {
  label: string;
  onClickAdd?: () => void;
  onChange: (e: React.SyntheticEvent<Element, Event>, value: T | null) => void;
  fetch: (query: string) => Promise<T[]>;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  value: T | null;
  required?: boolean;
  fetchAllOnLoad?: boolean;
  placeholder?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  disablePortal?: boolean;
  watchList?: (object | null)[];
  renderOption?: (
      props: React.HTMLAttributes<HTMLLIElement> & { key: any },
      option: any,
      state: AutocompleteRenderOptionState,
      ownerState: any
    ) => React.ReactNode;
  renderInput?: any;
  refetchToken?: any;
}

function AutocompleteWithThrottle<T>({
  value,
  isOptionEqualToValue,
  label,
  onClickAdd,
  fetch,
  getOptionLabel,
  onChange,
  disabled,
  required,
  fetchAllOnLoad = true,
  placeholder,
  size = 'medium',
  disablePortal = true,
  watchList,
  renderOption,
  refetchToken,
}: AutocompleteWithThrottleProps<T>) {
  const [matchingOptions, setMatchingOptions] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const debouncedFetchOptions = _debounce(async (inputQuery) => {
    const query = inputQuery ? inputQuery : '';

    if (!query) {
      setMatchingOptions([]);
      return;
    }

    try {
      setLoading(true);
      const options = await fetch(query);
      setMatchingOptions(options);
    } catch (error) {
      console.error('Error fetching options:', error);
    } finally {
      setLoading(false);
    }
  }, 500);

  const handleInputChange = (
    event: any,
    newInputValue: string,
    reason: string
  ) => {
    if (event && reason === 'input') {
      debouncedFetchOptions(newInputValue);
    }
  };

  const fetchAllOptions = async () => {
    try {
      setLoading(true);
      const options = await fetch('');
      setMatchingOptions(options); // Set all options initially
    } catch (error) {
      console.error('Error fetching options:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all options initially when the component mounts
  useEffect(() => {
    if (fetchAllOnLoad) fetchAllOptions();
  }, []);

  useEffect(() => {
    if (value == null) setMatchingOptions([]);
  }, [value]);

  return (
    <Autocomplete
      disablePortal={disablePortal}
      options={matchingOptions}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      onInputChange={handleInputChange}
      filterOptions={(x) => x}
      loading={loading}
      onChange={onChange}
      disabled={disabled}
      defaultValue={value ?? null}
      value={value ?? null}
      renderOption={renderOption}
      noOptionsText={"Search by phone"}
      renderInput={(params) => (
        <TextField
          autoComplete="off"
          multiline
          required={required !== undefined ? required : true}
          placeholder={placeholder}
          {...params}
          disabled={disabled}
          label={label}
          variant="outlined"
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: onClickAdd && (
              <InputAdornment position="start">
                <IconButton onClick={onClickAdd} disabled={disabled} size={size}>
                  <AddIcon fontSize={size === 'small' ? 'small' : 'medium'} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    />
  );
}

export default AutocompleteWithThrottle;
